"use client";

import { useState, type HTMLAttributes, type ReactNode } from "react";
import { Link } from "@/i18n/navigation";
import RichDescription from "@/app/components/RichDescription";
import { imageUrl } from "@/lib/image-url";
import CardImage from "@/app/components/CardImage";
import { displayName } from "@/lib/display-name";
import { useTryGameTranslations } from "@/lib/game-i18n";

export interface CardInfo {
  id: string;
  name: string;
  description: string;
  type: string;
  rarity: string;
  cost: number;
  color: string;
  image_url: string | null;
}

export interface RelicInfo {
  id: string;
  name: string;
  description: string;
  rarity: string;
  rarity_key?: string | null;
  image_url: string | null;
}

export interface PotionInfo {
  id: string;
  name: string;
  description: string;
  rarity: string;
  rarity_key?: string | null;
  image_url: string | null;
}

export { cleanId, displayName } from "@/lib/display-name";

export function CardPill({
  cardId,
  upgraded,
  enchantment,
  cardData,
  bp,
  className,
  children,
}: {
  cardId: string;
  upgraded?: boolean;
  enchantment?: string;
  cardData: Record<string, CardInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const [above, setAbove] = useState(true);
  const gt = useTryGameTranslations();
  const info = cardData[cardId];
  const name =
    gt(`cards.${cardId}.title`) ?? info?.name ?? displayName(`CARD.${cardId}`);
  const enchantmentName = enchantment
    ? (gt(`enchantments.${enchantment}.title`) ??
      displayName(`ENCHANTMENT.${enchantment}`))
    : undefined;
  return (
    <PillLink
      href={
        catalogKnows(cardData, cardId)
          ? `${bp}/cards/${cardId.toLowerCase()}`
          : null
      }
      className={`relative ${className || ""}`}
      onMouseEnter={(e) => {
        // Flip the card preview below when there isn't room above (small
        // screens / cards near the top, e.g. the deck modal on the live page).
        setAbove(e.currentTarget.getBoundingClientRect().top > 240);
        setShow(true);
      }}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? (
        <>
          {name}
          {upgraded && "+"}
          {enchantmentName && (
            <span className="text-[var(--color-necrobinder)] ml-1">
              [{enchantmentName}]
            </span>
          )}
        </>
      )}
      {show && (
        // Pop the full rendered card (enchanted and/or upgraded variant when
        // the run says so) instead of the text tooltip. Falls back from the
        // enchanted render to the plain one, then to the beta render (cards
        // that only exist on the beta channel yet), then to the portrait art.
        <span
          className={`pointer-events-none absolute z-50 left-1/2 w-40 -translate-x-1/2 ${
            above ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          <CardImage
            id={cardId}
            upgraded={!!upgraded}
            enchantment={enchantment}
            art={info?.image_url}
            className="w-40 h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.7)]"
          />
        </span>
      )}
    </PillLink>
  );
}

export function RelicPill({
  relicId,
  relicData,
  bp,
  className,
  children,
}: {
  relicId: string;
  relicData: Record<string, RelicInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const gt = useTryGameTranslations();
  const info = relicData[relicId];
  const name =
    gt(`relics.${relicId}.title`) ??
    info?.name ??
    displayName(`RELIC.${relicId}`);
  const rarity =
    (info?.rarity_key &&
      gt(`gameplay_ui.RELIC_RARITY.${info.rarity_key.toUpperCase()}`)) ||
    info?.rarity;
  return (
    <PillLink
      href={
        catalogKnows(relicData, relicId)
          ? `${bp}/relics/${relicId.toLowerCase()}`
          : null
      }
      className={`relative ${className || ""}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? name}
      {show && info && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none">
          <div className="flex items-start gap-2 mb-1.5">
            {info.image_url && (
              <img
                src={imageUrl(info.image_url)}
                alt=""
                className="w-8 h-8 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-xs text-[var(--text-primary)] truncate">
                {name}
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {rarity}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            <RichDescription text={info.description} />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" />
        </div>
      )}
    </PillLink>
  );
}

export function PotionPill({
  potionId,
  potionData,
  bp,
  className,
  children,
}: {
  potionId: string;
  potionData: Record<string, PotionInfo>;
  bp: string;
  className?: string;
  children?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const gt = useTryGameTranslations();
  const info = potionData[potionId];
  const name =
    gt(`potions.${potionId}.title`) ??
    info?.name ??
    displayName(`POTION.${potionId}`);
  const rarity =
    (info?.rarity_key &&
      gt(`gameplay_ui.POTION_RARITY.${info.rarity_key.toUpperCase()}`)) ||
    info?.rarity;
  return (
    <PillLink
      href={
        catalogKnows(potionData, potionId)
          ? `${bp}/potions/${potionId.toLowerCase()}`
          : null
      }
      className={`relative ${className || ""}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children ?? name}
      {show && info && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none">
          <div className="flex items-start gap-2 mb-1.5">
            {info.image_url && (
              <img
                src={imageUrl(info.image_url)}
                alt=""
                className="w-8 h-8 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div className="min-w-0">
              <div className="font-semibold text-xs text-[var(--text-primary)] truncate">
                {name}
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {rarity}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            <RichDescription text={info.description} />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" />
        </div>
      )}
    </PillLink>
  );
}

function catalogKnows(catalog: Record<string, unknown>, id: string) {
  return id in catalog || Object.keys(catalog).length === 0;
}

function PillLink({
  href,
  ...rest
}: { href: string | null } & HTMLAttributes<HTMLElement>) {
  return href ? <Link href={href} {...rest} /> : <span {...rest} />;
}
